// Picking a cell reference by pointing at it.
//
// Pure, and separate from the store so it is testable in plain node — the store
// imports the Giraffe SDK, which touches `window` at module load.

import { quoteSheetName, rowColToA1 } from './address';

export interface Pick {
  /** Where the inserted reference begins in the formula text. */
  start: number;
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

/** Characters a reference can legally follow. */
const AFTER = '=+-*/^(,&<>:%';

/**
 * Can a cell reference go in at the caret?
 *
 * Only right after something a reference can follow — `=`, an operator, an open
 * paren, a comma. Anywhere else an arrow key means "move the caret" and a click
 * means "I am done here", which is what keeps pointing from becoming a mode you
 * get stuck in halfway through typing a word.
 */
export function canPointAt(text: string, caret: number): boolean {
  if (!text.startsWith('=')) return false;
  const before = text.slice(0, caret).replace(/\s+$/, '');
  if (before === '=') return true;
  const last = before[before.length - 1];
  return last !== undefined && AFTER.includes(last);
}

/**
 * `A1` for a single cell, `A1:B2` for a range — normalised either way, and
 * qualified with a sheet name when the pick is on a different sheet from the
 * formula: `Sheet2!A1`, or `'My Sheet'!A1` when the name needs quoting.
 */
export function refText(pick: Pick, sheet?: string): string {
  const r1 = Math.min(pick.r1, pick.r2);
  const r2 = Math.max(pick.r1, pick.r2);
  const c1 = Math.min(pick.c1, pick.c2);
  const c2 = Math.max(pick.c1, pick.c2);
  const from = rowColToA1(r1, c1);
  const body = r1 === r2 && c1 === c2 ? from : `${from}:${rowColToA1(r2, c2)}`;
  return sheet === undefined ? body : `${quoteSheetName(sheet)}!${body}`;
}

/** Splice a pick's reference into the formula, replacing the previous one. */
export function applyPick(
  text: string,
  caret: number,
  pick: Pick,
  sheet?: string
): { text: string; caret: number } {
  const ref = refText(pick, sheet);
  return {
    text: `${text.slice(0, pick.start)}${ref}${text.slice(caret)}`,
    caret: pick.start + ref.length,
  };
}
